import React, { useEffect, useState } from 'react';

import { Button, ButtonGroup } from 'components/ui/button'
import { Modal } from 'components/ui/modal'

import { sharedActionCreators, SharedActionCreators } from 'containers/shared';
import { connect } from 'containers/store';

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore } from 'domains/org/getty/store';

import { Stop, StopPointOfInterest } from 'generated/mos/org/getty/audio'
import { CreateStopPointOfInterestRequest } from 'generated/mos/org/getty/audiomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

import { stopSchema } from '../../../schemas/stop'

import { Search } from '../../stops/search'
import { bffEndpoint } from 'helpers/bff'
import { RadioInputElement } from 'components/ui/radio'
import { Description, Image, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'
import { AttributeRef } from 'generated/mos/entity'
import { Media } from 'generated/mos/media'

type ListItemProps = {
  projectId: string;
  historyPush: (id: string) => void;
  stop: Stop.Entity;
};


type ConnectedProps = {
  stopPointOfInterestCreate: GettyStore['getty']['stopPointOfInterestCreate'];
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyCreateStopPointOfInterestRequest' | 'gettyCreateStopPointOfInterestReset'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  onCancel: () => void;
  onComplete: (pointOfInterest: StopPointOfInterest.Entity) => void;
  moodRef: CreateStopPointOfInterestRequest.Entity['moodRef'];
  projectId: string;
  historyPush: (id: string) => void;
};

type Props = ConnectedProps & ActionProps & DirectProps;

const PointOfInterestCreateView = (props: Props) => {
  const {
    gettyCreateStopPointOfInterestRequest,
    gettyCreateStopPointOfInterestReset,
    onComplete,
    stopPointOfInterestCreate,
    toastNotification,
    projectId,
    historyPush,
  } = props

  const [ stopSelected, setStopSelected ] = useState<Stop.Entity | undefined>()

  const onToggleEntity = (stop: Stop.Entity) => {
    const selectedRef = stopSelected && Stop.isRef(stopSelected.ref) ? stopSelected.ref.id : undefined

    // it's currently selected, so unselect it.
    if (selectedRef && selectedRef === Stop.mustRef(stop.ref).id) {
      setStopSelected(Stop.defaults)
    } else {
      // update the parent with the entity which has been selected.
      setStopSelected(stop)
    }
  }

  const listItem = ({stop}: ListItemProps) => {
    const selected =
      stopSelected &&
      stopSelected.ref &&
      Stop.mustRef(stopSelected.ref).id === Stop.mustRef(stop.ref).id

    const hostName = bffEndpoint(window.location.hostname)
    let title: any = stopSchema.title.read(stop.title)
    let producedBy: any = stopSchema.producedBy.read(stop.producedBy)
    const stopImage = stopSchema.image.read(stop.image) as Media.Ref
    const stopImageId = stopImage && stopImage.id
    const imagePath = stopImageId && hostName ? `${hostName}/images/${stopImageId}.jpg?s=crop&h=92&w=92` : ''
    if (title && title.type === AttributeRef.refName) {
      title = ''
    }
    // check if Attribute ref resolved to a string value
    if (producedBy && producedBy.type === AttributeRef.refName) {
      producedBy = undefined
    }

    return (
      <ListItemLabel key={Stop.mustRef(stop.ref).id}>
        <RadioInputElement checked={selected} onChange={() => onToggleEntity(stop)} name={Stop.mustRef(stop.ref).id}  />
        <Image src={imagePath} />
        <Description>
          <Title>{title}</Title>
          <Subtitle>{`${producedBy ? producedBy : '-'}`}</Subtitle>
        </Description>
      </ListItemLabel>
    )
  }

  const createPointOfInterest = () => {
    if (stopSelected && stopPointOfInterestCreate.status === Status.Idle) {
      gettyCreateStopPointOfInterestRequest({
        ...CreateStopPointOfInterestRequest.defaults,
        type: CreateStopPointOfInterestRequest.refName,
        stopRef: stopSelected.ref,
        moodRef: props.moodRef
      })
    }

    if (stopPointOfInterestCreate.status === Status.Failed) {
      toastNotification({
        type: 'error',
        text: 'Could not create pointOfInterest',
      });
      gettyCreateStopPointOfInterestReset()
    }
  }

  useEffect(() => {
    if (stopPointOfInterestCreate.status === Status.Ready) {
      onComplete(stopPointOfInterestCreate.data);
      gettyCreateStopPointOfInterestReset();
    }
  }, [ stopPointOfInterestCreate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      onClose={props.onCancel}
      header={<h3>Find Stop</h3>}
      footer={(
        <ButtonGroup>
          <Button onClick={props.onCancel} appearance="secondary">Cancel</Button>
          <Button isDisabled={!stopSelected} onClick={createPointOfInterest} >
            Create Point of Interest
          </Button>
        </ButtonGroup>
      )}
    >
      <Search projectId={projectId} historyPush={historyPush} listItem={listItem} />
    </Modal>
  );
};

export const PointOfInterestCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  store => ({
    stopPointOfInterestCreate: store.getty.stopPointOfInterestCreate
  }),
  {
    ...pick(actionCreators, 'gettyCreateStopPointOfInterestRequest', 'gettyCreateStopPointOfInterestReset'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(PointOfInterestCreateView)
